import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { v4 as uuidv4 } from "uuid";

import CreatableSelect from "react-select/creatable";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import Dropdown from "react-bootstrap/Dropdown";
import Nav from "react-bootstrap/Nav";
import toast from "react-hot-toast";
import StudentDetails from "../components/StudentDetails";
import ToastDisplay from "../components/ToastDisplay";
import InfoLogs from "../components/InfoLogs";
import FileUpload from "../components/FileUpload";

const Student = () => {
	let { id } = useParams();
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [loading, setLoading] = useState(true);
	const [student, setStudent] = useState(null);
	const [editModal, setEditModal] = useState(false);
	const [deleteModal, setDeleteModal] = useState(false);
	const [passwordModal, setPasswordModal] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [levelLoading, setLevelLoading] = useState(false);
	const [levels, setLevels] = useState([]);

	const [name, setName] = useState("");
	const [level, setLevel] = useState({});
	const [studentNo, setStudentNo] = useState("");
	const [rfidNo, setRfidNo] = useState("");
	const [msisdn, setMsisdn] = useState("");
	const [photo, setPhoto] = useState(null);
	const [photoPath, setPhotoPath] = useState("");

	const [errors, setErrors] = useState([]);
	const [tab, setTab] = useState("details");

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "studentNo", value: studentNo },
		{ key: "rfidNo", value: rfidNo },
		{ key: "msisdn", value: msisdn },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/s/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setLevels(
				data.levels.map((level) => ({
					value: level.name,
					label: level.name,
				}))
			);
			setStudent(data.student);
			setName(data.student.name);
			setStudentNo(data.student.student_no);
			setRfidNo(data.student.id_no);
			setMsisdn(data.student.msisdn);
			setPhotoPath(data.student.photo || "");
			setLevel({
				value: data.student.level,
				label: data.student.level,
			});
		}
		setLoading(false);
	};

	const editStudent = async () => {
		setActionLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				student_no: studentNo,
				id_no: rfidNo,
				level: level?.value || "",
				msisdn: msisdn,
				photo: photoPath,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/s/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			getData();
			closeEditModal();
		}
		setActionLoading(false);
	};

	const deleteStudent = async () => {
		setDeleteLoading(true);
		const requestOptions = {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/s/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			navigate("/students");
		}
		setDeleteModal(false);
		setDeleteLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		editStudent();
	};

	const handlePhoto = (e) => {
		const file = e.target.files[0];
		const newUid = uuidv4();
		const uuid_name = `${newUid}.${file.name.split(".").pop()}`;

		const forUpload = {
			uuid: uuid_name,
			file: file,
		};
		setPhoto(forUpload);
		setPhotoPath(
			`${process.env.REACT_APP_STORAGE_URL}/pisara/students/${uuid_name}`
		);
	};

	const closeEditModal = () => {
		setEditModal(false);
		setName(student.name);
		setStudentNo(student.student_no);
		setRfidNo(student.id_no);
		setMsisdn(student.msisdn);
		setPhotoPath(student.photo || "");
		setPhoto(null);
		setLevel({
			value: student.level,
			label: student.level,
		});

		setErrors([]);
	};

	const createLevel = async (inputValue) => {
		setLevelLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				levels: [inputValue],
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/students/levels/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			setLevels([...levels, { value: inputValue, label: inputValue }]);
			setLevel({ value: inputValue, label: inputValue });
		}
		setLevelLoading(false);
	};

	useEffect(() => {
		if (profile) {
			if (!profile.role.students_view) {
				navigate("/");
			} else {
				if (id) {
					getData();
				}
			}
		}
	}, [id, profile]);

	return (
		<>
			<Container className="gx-5">
				<div className="info-page-header mb-10">
					<a
						href="#"
						className="back-btn flex-row-left fit mb-20"
						onClick={(e) => {
							e.preventDefault();
							navigate(-1);
						}}
					>
						<i className="ri-arrow-left-line opacity-7" />
						<span className="text-sm text-regular-inter upper opacity-7">
							Students
						</span>
					</a>
					<Row className="flex-row-left mb-30">
						<Col className="flex-row-left">
							<div
								className="contact-icon flex-column rounded-8 bg-cover border"
								style={{ background: `url(${student?.photo})` }}
							>
								{!student?.photo && (
									<span className="text-bold-gotham text-md">
										{student?.name[0]}
									</span>
								)}
							</div>
							<h1 className="text-lg mb-0 text-medium-gotham">
								{loading ? (
									<Skeleton
										width={250}
										height={25}
										baseColor="#dadee7"
										highlightColor="#e9ecf2"
									/>
								) : (
									student?.name || "--"
								)}
							</h1>
						</Col>
						<Col className="flex-row-right">
							{profile?.role?.students_edit ||
							profile?.role?.students_delete ? (
								<Dropdown>
									<Dropdown.Toggle
										variant="light"
										className="info-control-btn text-lg rounded-4 flex-column no-arrow"
										disabled={loading}
									>
										<i className="ri-more-2-fill me-0" />
									</Dropdown.Toggle>
									<Dropdown.Menu className="text-sm dropdown-default">
										{profile?.role?.students_edit && (
											<>
												<Dropdown.Item
													href="#"
													onClick={(e) => {
														e.preventDefault();
														setEditModal(true);
													}}
												>
													Edit
												</Dropdown.Item>
											</>
										)}
										{profile?.role?.students_delete && (
											<Dropdown.Item
												href="#"
												className="text-danger"
												onClick={() => setDeleteModal(true)}
											>
												Delete
											</Dropdown.Item>
										)}
									</Dropdown.Menu>
								</Dropdown>
							) : (
								""
							)}
						</Col>
					</Row>
				</div>
				<Nav variant="tabs" defaultActiveKey={tab} className="mb-20">
					<Nav.Item>
						<Nav.Link
							eventKey="details"
							className="info-tab-item"
							onClick={() => setTab("details")}
						>
							Details
						</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link
							eventKey="logs"
							className="info-tab-item"
							onClick={() => setTab("logs")}
						>
							Logs
						</Nav.Link>
					</Nav.Item>
				</Nav>
				{tab === "details" && (
					<StudentDetails loading={loading} student={student} />
				)}
				{tab === "logs" && <InfoLogs loading={loading} data={student} />}
			</Container>
			<Modal
				show={editModal}
				className="info-modal"
				size="lg"
				centered
				onHide={closeEditModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Edit Student</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-30 full flex-column">
							<div className="file-upload-container">
								{photoPath && !photo && (
									<div
										className="file-upload bg-cover rounded-4 border"
										style={{
											background: `url(${photoPath})`,
										}}
									>
										<div className="file-delete-overlay flex-column">
											<i
												className="ri-delete-bin-line text-white m-0 text-lg"
												onClick={() => setPhotoPath("")}
											/>
										</div>
									</div>
								)}
								{photo && (
									<FileUpload
										file={photo}
										files={photo}
										setFiles={setPhoto}
										filePaths={photoPath}
										setFilePaths={setPhotoPath}
										directory="pisara/students"
									/>
								)}
								{!photoPath && (
									<div
										className="file-btn border rounded-8"
										onClick={() => photoRef.current.click()}
									>
										<div className="overlay" />
										<img
											src="/default-user.png"
											alt=""
											className="default-user-photo"
										/>
									</div>
								)}
								<input
									ref={photoRef}
									type="file"
									className="hidden"
									accept="image/*"
									onChange={handlePhoto}
								/>
							</div>
						</div>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Student Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-50">
							<Col>
								<Form.Label className="text-sm">Student No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("studentNo") && "border-danger"
									}`}
									value={studentNo}
									onChange={(e) => setStudentNo(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">Grade/Level</Form.Label>
								<CreatableSelect
									isClearable
									isDisabled={levelLoading}
									isLoading={levelLoading}
									onCreateOption={createLevel}
									value={level}
									onChange={setLevel}
									options={levels}
									menuShouldScrollIntoView={false}
									maxMenuHeight={200}
									placeholder=""
									styles={{
										control: (baseStyles, state) => ({
											...baseStyles,
											height: "38px",
											outline: "none",
											borderRadius: 4,
											fontSize: "0.875rem",
											borderColor: state.isFocused
												? "#86b7fe !important"
												: "#dee2e6 !important",
											boxShadow: state.isFocused
												? "0 0 0 .25rem rgba(13, 110, 253, .25)"
												: "none",
										}),
										option: (baseStyles, state) => ({
											...baseStyles,
											fontSize: ".875rem",
											color: "#050911",
											backgroundColor: state.isFocused
												? "#c8deff !important"
												: "#fff",
										}),
									}}
								/>
							</Col>
						</Row>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Notification Settings
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">
									Guardian's Mobile No.
								</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("msisdn") && "border-danger"
									}`}
									value={msisdn}
									onChange={(e) => setMsisdn(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">RFID No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("rfidNo") && "border-danger"
									}`}
									value={rfidNo}
									onChange={(e) => setRfidNo(e.target.value)}
								/>
							</Col>
						</Row>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm me-2"
							onClick={closeEditModal}
						>
							Close
						</Button>
						<Button
							type="submit"
							variant="primary"
							className="btn-sm action-btn text-sm btn-default"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Save Changes"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
			<Modal show={deleteModal} onHide={() => setDeleteModal(false)}>
				<Modal.Body>
					<h2 className="text-lg text-medium-inter mb-15">Delete Student</h2>
					<p className="text-sm opacity-7">
						Are you sure you want to delete this student? This action cannot be
						undone.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="light"
						className="modal-btn btn-sm dismiss-btn text-medium-inter text-sm"
						onClick={() => setDeleteModal(false)}
					>
						Cancel
					</Button>
					<Button
						variant="danger"
						className="action-btn btn-sm text-medium-inter text-sm flex-column"
						disabled={deleteLoading}
						onClick={deleteStudent}
					>
						{deleteLoading ? (
							<Spinner className="btn-loader text-white" />
						) : (
							"Delete"
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default Student;
